import { useParams } from 'react-router';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { OffstreetTransactionActions } from './OperatorOffstreetTransactionTypes.ts';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import {
    NoReceiptForRunningContract,
    OffstreetTransactionActionText,
    OffstreetTransactionOpen,
    OperatorOffstreetTransactionDetailRoutedModal,
} from './OperatorOffstreetTransactionShared.tsx';
import { Box } from '@mui/material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { TicketLostIdentificationIcon } from './OperatorOffstreetTransactionsRoute.tsx';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

type TicketListOffstreetTransactionDetail = {
    payStationPaymentId: string;
    ticketLostCode: string | null;
    exitedAt: string | null;
    zoneName: string;
    amountPaid: number;
    paidAt: string;
    paymentChannel: PaymentChannel;
    createdAt: string;
    receiptReady: boolean;
};

export function OperatorOffstreetTransactionTicketLost() {
    const { operatorExternalId } = useOperatorContext();
    const urlParams = useParams<{ payStationPaymentId: string }>();
    const payStationPaymentId = urlParams.payStationPaymentId
        ? urlParams.payStationPaymentId
        : null;
    const [offstreetTransactionsState] = useServerFetch<
        TicketListOffstreetTransactionDetail,
        { payStationPaymentId: string },
        null
    >(
        context => ({
            url: `/ui-api/operator-account/offstreet-transactions/ticket-lost/${context.payStationPaymentId}`,
        }),
        payStationPaymentId ? { payStationPaymentId } : null,
    );

    return (
        <>
            <OperatorOffstreetTransactionDetailRoutedModal
                render={controller => (
                    <OperatorAsyncLoadedSection
                        requestState={offstreetTransactionsState}
                        render={transaction => (
                            <OperatorRoutedModalContent
                                header={
                                    <HeaderComponent
                                        title="Ticket"
                                        editMode={false}
                                        buttons={
                                            <HeaderActionCloseButtons
                                                onClose={controller.close}
                                                dropdownItems={[
                                                    {
                                                        key: 'download-receipt',
                                                        label: (
                                                            <OffstreetTransactionActionText
                                                                action={
                                                                    OffstreetTransactionActions.DOWNLOAD_RECEIPT
                                                                }
                                                            />
                                                        ),
                                                        onClickCallback: () => {
                                                            if (
                                                                transaction.receiptReady
                                                            ) {
                                                                openNewTab(
                                                                    urlWithOperatorExternalId(
                                                                        `/ui-api/operator-account/pay-station-payment/${transaction.payStationPaymentId}/receipt`,
                                                                        operatorExternalId,
                                                                    ),
                                                                );
                                                            }
                                                        },
                                                        disabled:
                                                            !transaction.receiptReady,
                                                        tooltip:
                                                            !transaction.receiptReady ? (
                                                                <NoReceiptForRunningContract />
                                                            ) : undefined,
                                                    },
                                                ]}
                                            />
                                        }
                                    />
                                }
                                body={
                                    <FormContentLayout>
                                        <FormLeftColumn>
                                            <OffstreetTransactionOpen
                                                isPresent={
                                                    !transaction.exitedAt
                                                }
                                                hasAntipassback={false}
                                            />
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Identifikation"
                                                        fr="Identification"
                                                        it="Identificazione"
                                                        en="Identification"
                                                    />
                                                }
                                                value={
                                                    <TicketLostIdentificationIcon
                                                        ticketLostCode={
                                                            transaction.ticketLostCode
                                                        }
                                                    />
                                                }
                                            />
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Einfahrt"
                                                        fr="Entrée"
                                                        it="Entrata"
                                                        en="Entry"
                                                    />
                                                }
                                                value={EmDash}
                                            />
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Ausfahrt"
                                                        fr="Sortie"
                                                        it="Uscita"
                                                        en="Exit"
                                                    />
                                                }
                                                value={
                                                    transaction.exitedAt
                                                        ? Formatter.dayMonthYearHourMinute(
                                                              DateTime.fromISO(
                                                                  transaction.exitedAt,
                                                              ),
                                                          )
                                                        : EmDash
                                                }
                                            />
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Dauer"
                                                        fr="Durée"
                                                        it="Durata"
                                                        en="Duration"
                                                    />
                                                }
                                                value={EmDash}
                                            />
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Parking"
                                                        fr="Parking"
                                                        it="Parcheggio"
                                                        en="Parking"
                                                    />
                                                }
                                                value={transaction.zoneName}
                                            />
                                        </FormLeftColumn>
                                        <FormRightColumn>
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Bezahlt"
                                                        fr="Payé"
                                                        it="Pagato"
                                                        en="Paid"
                                                    />
                                                }
                                                value={`CHF ${transaction.amountPaid.toFixed(2)}`}
                                            />
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Kanal"
                                                        fr="Canal"
                                                        it="Canale"
                                                        en="Channel"
                                                    />
                                                }
                                                value={
                                                    <PaymentChannelLocalized
                                                        paymentChannel={
                                                            PaymentChannel.PAY_STATION
                                                        }
                                                    />
                                                }
                                            />
                                            <Box
                                                sx={{
                                                    flexGrow: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'end',
                                                }}
                                            >
                                                <BlockLabel
                                                    label={
                                                        <Localized
                                                            de="Erstellt am"
                                                            fr="Créé le"
                                                            it="Creato il"
                                                            en="Created at"
                                                        />
                                                    }
                                                >
                                                    {Formatter.dayMonthYearHourMinute(
                                                        DateTime.fromISO(
                                                            transaction.createdAt,
                                                        ),
                                                    )}
                                                </BlockLabel>
                                                <BlockLabel label="ID">
                                                    {
                                                        transaction.payStationPaymentId
                                                    }
                                                </BlockLabel>
                                            </Box>
                                        </FormRightColumn>
                                    </FormContentLayout>
                                }
                            />
                        )}
                    />
                )}
            />
        </>
    );
}
